import { useEffect, useState, useMemo } from 'react';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { templatesUseI18n } from './i18n';
import CardStats from '../../components/CardStats/CardStats';
import PerCategory from './partials/PerCategory/PerCategory';
import {
  getDocumentUsageByArea,
  getDocumentUsageByService,
  getDocumentUsageByUser,
  getDocumentUsageReport,
  getDocumentUsageStats,
} from '../../services/templatesUseServices';
import { sortByLocale, sortByNumber } from '../../utils/sortBy';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { getCurrentUser } from '../../services/authService';
import { excelExportLogService } from 'services/excelLogFromFront';
import usePermissionsReports from 'permissions/modules/reports/usePermissionsReports';

const SORT_BACKEND_KEYS = {
  templateName: 'Nombre',
  service: 'Servicio',
  totalUses: 'Uso total',
  mainUser: 'Usuario principal',
  mainArea: 'Área principal',
};

const TemplatesUse = ({ DesignSystemComponents }) => {
  const { Card, Table, Button } = DesignSystemComponents;

  const { t } = useScopedTranslation(templatesUseI18n);

  const [loading, setLoading] = useState(false);
  const [totalGeneratedStat, setTotalGeneratedStat] = useState('');
  const [totalActivesStat, setTotalActivesStat] = useState('');
  const [report, setReport] = useState([]);
  const [page, setPage] = useState(0);

  const [servicesUsage, setServicesUsage] = useState([]);
  const [areasUsage, setAreasUsage] = useState([]);
  const [usersUsage, setUsersUsage] = useState([]);

  const { canDownloadTemplateUsageExcel } = usePermissionsReports();

  const [userId, setUserId] = useState(null);

  const [sortApi, setSortApi] = useState({
    sortKey: null,
    sortDesc: null,
  });

  const splitNameAndNumber = (text = '') => {
    const index = text.indexOf('(');
    return {
      nombre: index !== -1 ? text.substring(0, index).trim() : text,
      numero: index !== -1 ? text.substring(index).trim() : '',
    };
  };

  const sortFns = {
    templateName: sortByLocale('templateName'),
    service: sortByLocale('service'),
    totalUses: sortByNumber('totalUses'),
    mainUser: sortByLocale('mainUser'),
    mainArea: sortByLocale('mainArea'),
  };

  useEffect(() => {
    const fetchStatsAndReport = async () => {
      try {
        setLoading(true);

        const stats = await getDocumentUsageStats.getStats();
        const reportResult = await getDocumentUsageReport.getReport();

        setTotalGeneratedStat(stats.totalGenerated);
        setTotalActivesStat(stats.activeTemplates);
        setReport(reportResult.items);
      } catch (error) {
        console.warn('Could not fetch data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStatsAndReport();
  }, []);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);

      try {
        const [services, areas, users] = await Promise.all([
          getDocumentUsageByService.getByService(),
          getDocumentUsageByArea.getByArea(),
          getDocumentUsageByUser.getByUser(),
        ]);

        setServicesUsage(services);
        setAreasUsage(areas);
        setUsersUsage(users);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, []);

  const sortedReport = useMemo(() => {
    if (!sortApi.sortKey) return report;

    const frontendKey = Object.entries(SORT_BACKEND_KEYS).find(
      ([, backend]) => backend === sortApi.sortKey
    )?.[0];

    if (!frontendKey) return report;

    const sorted = [...report].sort((a, b) => {
      const aVal = a[frontendKey];
      const bVal = b[frontendKey];

      if (typeof aVal === 'string') {
        return aVal.localeCompare(bVal, undefined, { sensitivity: 'base' });
      }

      return aVal - bVal;
    });

    return sortApi.sortDesc ? sorted.reverse() : sorted;
  }, [report, sortApi]);

  const downloadExcel = async () => {
    try {
      await excelExportLogService.log({
        userId,
        source: t('tu_excelSource'),
        sortKey: sortApi.sortKey,
        sortDesc: sortApi.sortDesc,
        filters: {},
      });
    } catch (error) {
      console.warn('Export log failed', error);
    }

    if (!sortedReport.length) return;

    const formatted = sortedReport.map((item) => {
      const { nombre: userName, numero: userCount } = splitNameAndNumber(
        item.mainUser
      );
      const { nombre: areaName, numero: areaCount } = splitNameAndNumber(
        item.mainArea
      );

      return {
        [t('tu_excelCol_name')]: item.templateName,
        [t('tu_excelCol_service')]: item.service,
        [t('tu_excelCol_totalUses')]: item.totalUses,
        [t('tu_excelCol_mainUser')]: `${userName} ${userCount}`,
        [t('tu_excelCol_mainArea')]: `${areaName} ${areaCount}`,
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(formatted);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, t('tu_excelSheet'));

    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });

    const blob = new Blob([excelBuffer], {
      type: 'application/octet-stream',
    });

    saveAs(blob, t('tu_excelFile'));
  };

  useEffect(() => {
    const authUser = getCurrentUser();
    if (authUser?.userId) {
      setUserId(authUser.userId);
    }
  }, []);

  return (
    <div>
      <div className='mb-4'>
        <div className='row'>
          <div className='col-9'>
            <h2 className='fw-semibold'>{t('tu_pageTitle')}</h2>
            <h4 className='fw-normal'>{t('tu_pageSubtitle')}</h4>
          </div>
        </div>
      </div>
      <div className='row mb-4'>
        <div className='col-12 col-md-6'>
          <CardStats
            DesignSystemComponents={DesignSystemComponents}
            title={t('tu_statTotalGenerated')}
            count={totalGeneratedStat}
            subtitle=''
            loading={loading}
          />
        </div>
        <div className='col-12 col-md-6'>
          <CardStats
            DesignSystemComponents={DesignSystemComponents}
            title={t('tu_statActiveTemplates')}
            count={totalActivesStat}
            subtitle=''
            loading={loading}
          />
        </div>
      </div>
      <Card specialPadding='px-4 pt-4 pb-2' className='mb-4'>
        <div className='row'>
          <div className='col-10 d-flex align-items-center'>
            <h3 className='fw-semibold mb-3'>{t('tu_tableTitle')}</h3>
          </div>
          <div className='col-2 d-flex justify-content-end'>
            <Button
              iconStart='download'
              label={t('tu_downloadExcel')}
              variant='naked'
              size='sm'
              className='mb-4'
              onClick={downloadExcel}
              disabled={!canDownloadTemplateUsageExcel}
            />
          </div>
        </div>
        <div className='table-container table-template'>
          <Table
            columns={[
              { accessor: 'templateName', label: t('tu_col_name'), sort: true },
              { accessor: 'service', label: t('tu_col_service'), sort: true },
              { accessor: 'totalUses', label: t('tu_col_totalUses'), sort: true },
              {
                accessor: 'mainUser',
                label: t('tu_col_mainUser'),
                render: (info) => {
                  const { nombre, numero } = splitNameAndNumber(info.mainUser);
                  return (
                    <div className='d-flex align-items-center'>
                      <p className='caption fw-normal  me-2'>
                        {nombre}{' '}
                        {numero && (
                          <span className='mini font-light'>{numero}</span>
                        )}
                      </p>
                    </div>
                  );
                },
                sort: true,
              },
              {
                accessor: 'mainArea',
                label: t('tu_col_mainArea'),
                render: (info) => {
                  const { nombre, numero } = splitNameAndNumber(info.mainArea);
                  return (
                    <div className='d-flex align-items-center'>
                      <p className='caption fw-normal  me-2'>
                        {nombre}{' '}
                        {numero && (
                          <span className='mini font-light'>{numero}</span>
                        )}
                      </p>
                    </div>
                  );
                },
                sort: true,
              },
            ]}
            data={sortedReport}
            page={page}
            onPageChange={setPage}
            initialPageSize={5}
            paginator
            paginatorGroup={[
              { label: '5', value: 5 },
              { label: '10', value: 10 },
            ]}
            loading={loading}
            sortFns={sortFns}
            onClientSortChange={({ sortKey, direction }) => {
              if (!sortKey || !direction) {
                setSortApi({ sortKey: null, sortDesc: null });
                return;
              }

              setSortApi({
                sortKey: SORT_BACKEND_KEYS[sortKey],
                sortDesc: direction === 'desc',
              });
            }}
          />
        </div>
      </Card>
      <div className='row'>
        <div className='col-12 col-md-4'>
          <PerCategory
            DesignSystemComponents={DesignSystemComponents}
            category={t('tu_category_service')}
            title={t('tu_usageBy', { category: t('tu_category_service') })}
            data={servicesUsage}
          />
        </div>
        <div className='col-12 col-md-4'>
          <PerCategory
            DesignSystemComponents={DesignSystemComponents}
            category={t('tu_category_area')}
            title={t('tu_usageBy', { category: t('tu_category_area') })}
            data={areasUsage}
          />
        </div>
        <div className='col-12 col-md-4'>
          <PerCategory
            DesignSystemComponents={DesignSystemComponents}
            category={t('tu_category_lawyer')}
            title={t('tu_usageBy', { category: t('tu_category_lawyer') })}
            data={usersUsage}
          />
        </div>
      </div>
    </div>
  );
};

export default TemplatesUse;
